import { useClinicTelecomTracking } from '@/hooks/analytics/useClinicTelecomTracking';
import useCalendarBooking from '@/hooks/consultations/useCalcomBooking';
import { generateCalcomActionEmail } from '@/pages/consultations/utils';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { Stack, Typography } from '@montugroup/design-system';
import type { ConsultationResponseDto } from '@montugroup/pms-api-contracts';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VideoChatOutlinedIcon from '@mui/icons-material/VideoChatOutlined';
import { useMediaQuery } from '@mui/material';
import { DateTime, IANAZone } from 'luxon';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ConsultationWaitlistSection } from './ConsultationWaitlistSection';
import {
  AccessMessage,
  BoldText,
  ConsultationContainer,
  DetailText,
  ManageButton,
  StartButton,
  StartButtonLink,
  StyledIcon,
  VisuallyHidden,
} from './UpcomingConsultationCard.styles';

// UpcomingConsultationLoadingSkeleton imports the container from here
export { ConsultationContainer } from './UpcomingConsultationCard.styles';

// The timezone the patient chose when booking, which the waitlist's preference bands use too, or the browser's when
// it's missing or not one the browser recognises, so a bad value can't turn every time into "Invalid DateTime".
const consultationZone = (timezone: string | null): string | undefined =>
  timezone && IANAZone.isValidZone(timezone) ? timezone : undefined;

// the page focuses a card by this id once the post-booking dialog closes
export const consultationCardId = (consultationId: number) => `upcoming-consultation-${consultationId}`;

interface ConsultationTileProps {
  consultation: ConsultationResponseDto;
  refetchConsultations: () => void;
}

export function UpcomingConsultationCard({ consultation, refetchConsultations }: ConsultationTileProps) {
  const zone = consultationZone(consultation.timezone);
  const startTime = DateTime.fromISO(consultation.startTime, { zone });
  const endTime = DateTime.fromISO(consultation.endTime, { zone });
  const [showJoinMessage, setShowJoinMessage] = useState(startTime.diff(DateTime.now(), 'minutes').minutes > 5);
  const themeColourScheme = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';

  const { data: user } = useUser();

  const { origin } = useCalendarBooking({
    handleBookingSuccess: refetchConsultations,
    handleRescheduleSuccess: refetchConsultations,
    handleCancelSuccess: refetchConsultations,
    theme: themeColourScheme,
  });

  const { trackStartConsultation } = useClinicTelecomTracking();

  // a waitlist change can remove the focused control, or the whole section once the refetch lands, so focus moves to
  // the card and the status line says what happened; the key makes a repeated message a new announcement
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [waitlistStatus, setWaitlistStatus] = useState<{ message: string; key: number } | null>(null);
  const announceWaitlistOutcome = useCallback(
    (message: string) => setWaitlistStatus((previous) => ({ message, key: (previous?.key ?? 0) + 1 })),
    [],
  );

  useEffect(() => {
    if (waitlistStatus) {
      cardRef.current?.focus();
    }
  }, [waitlistStatus]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setShowJoinMessage(startTime.diff(DateTime.now(), 'minutes').minutes > 5);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [startTime]);

  const formatConsultationUrl = (videoConsultationLink: string): string => {
    const url = new URL(videoConsultationLink);
    url.searchParams.set('utm_medium', 'patient-portal');
    return url.toString();
  };

  const formattedTime = `${startTime.toFormat('hh:mm a')} - ${endTime.toFormat('hh:mm a')} (${endTime.zoneName})`;

  const calcomActionEmailUser = user?.id ? `?${generateCalcomActionEmail(user.id)}` : '';
  const manageButtonCalcomLink = `booking/${consultation.calcomBookingUuid}${calcomActionEmailUser}`;

  const handleTrackStartConsultation = useCallback(() => {
    trackStartConsultation(consultation.id);
  }, [trackStartConsultation, consultation.id]);

  return (
    <ConsultationContainer
      ref={cardRef}
      id={consultationCardId(consultation.id)}
      role="group"
      aria-labelledby={titleId}
      tabIndex={-1}
      direction="column"
      spacing={3}
      alignItems="flex-start"
      sx={{ outline: 'none' }}
    >
      <Typography id={titleId} variant="subtitle2" sx={{ lineHeight: '2.5rem' }}>
        {consultation.type === 'Initial' ? 'Medical' : 'Follow up'} doctor consult
      </Typography>
      <Stack direction="row" alignItems="center" gap={1} width="100%">
        <StyledIcon>
          <AccountCircleIcon />
        </StyledIcon>
        {consultation?.practitioner?.name && <DetailText variant="body1">{consultation.practitioner.name}</DetailText>}
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} width="100%" data-chromatic="ignore">
        <StyledIcon>
          <CalendarTodayOutlinedIcon />
        </StyledIcon>
        <DetailText variant="body1">{startTime.toFormat('EEEE, dd LLLL')}</DetailText>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} width="100%" data-chromatic="ignore">
        <StyledIcon>
          <ScheduleIcon />
        </StyledIcon>
        <DetailText variant="body1">{formattedTime}</DetailText>
      </Stack>
      <Stack direction="row" alignItems="center" width="100%" data-chromatic="ignore">
        <StyledIcon>
          <VideoChatOutlinedIcon />
        </StyledIcon>
        <ManageButton
          data-cal-link={manageButtonCalcomLink}
          data-cal-config={{}}
          data-cal-origin={origin}
          data-cal-theme-mode={themeColourScheme}
        >
          Manage, reschedule or cancel your booking
        </ManageButton>
      </Stack>
      <ConsultationWaitlistSection consultation={consultation} onOutcome={announceWaitlistOutcome} />
      <VisuallyHidden role="status">
        {waitlistStatus && <span key={waitlistStatus.key}>{waitlistStatus.message}</span>}
      </VisuallyHidden>
      <DetailText variant="body1">
        <BoldText>Consultation I.D:</BoldText> {consultation.code}
      </DetailText>
      {consultation?.videoMeetingLink && (
        <Stack direction="column" alignItems="center" alignSelf="stretch">
          <StartButtonLink href={formatConsultationUrl(consultation.videoMeetingLink)} target="_blank">
            <StartButton variant="contained" color="primary" onClick={handleTrackStartConsultation}>
              Start pre-consult check
            </StartButton>
          </StartButtonLink>

          {showJoinMessage && (
            <AccessMessage>
              You can join the waiting room <BoldText>5 minutes</BoldText> prior to your scheduled consult time.
            </AccessMessage>
          )}
        </Stack>
      )}
    </ConsultationContainer>
  );
}
